/**
 * ONE catalog of DFY build steps for every surface.
 *
 * The runner's checkpoint ledger is the truth about what a build DOES; this
 * file is the server-side copy of that truth, versioned, so an estimate is
 * always computed against the step list the run actually followed. Before
 * this, the staff console kept its own hand-typed arrays (the e-mail gate
 * listed 10 steps while the builder reported 20, and the campaign phases were
 * labelled from their raw slug), which meant "step 7 of 10" could not be
 * trusted and no honest remaining-time estimate could be built on it.
 *
 * Rules:
 * - `version` changes whenever the ordered step list changes. A run stores the
 *   version it ran under; statistics are pooled per version-compatible step,
 *   never across a renamed or reordered catalog.
 * - `required` steps must all appear in a historical run for it to count as a
 *   valid sample. Optional steps are the ones a build legitimately skips
 *   (campaign phases depend on the campaign shape, photos on the client
 *   having sent any).
 * - `stepClass` is the ONLY thing a cross-gate prior may pool on. Priors stay
 *   inside a class so strategy `gates` can never stand in for salespage
 *   `assembly`.
 */

export type DfyGateKey = "strategy" | "salespage" | "emails" | "webinar";

/** Semantic classes for the cross-gate prior (report 2026-08-04). */
export type DfyStepClass = "prep" | "generate" | "critique" | "qa" | "assembly" | "delivery";

export interface DfyCatalogStep {
  key: string;
  required: boolean;
  stepClass: DfyStepClass;
}

export interface DfyStepCatalog {
  gateKey: DfyGateKey;
  version: string;
  steps: DfyCatalogStep[];
}

function step(key: string, stepClass: DfyStepClass, required = true): DfyCatalogStep {
  return { key, required, stepClass };
}

const STRATEGY: DfyStepCatalog = {
  gateKey: "strategy",
  version: "strategy@1",
  steps: [
    step("knowledge-pack", "prep"),
    // Newer than the historical ledgers (N=0 in the 2026-08-04 snapshot):
    // requiring it would invalidate every sample we have.
    step("instance-soul", "prep", false),
    step("audit-brief", "prep"),
    step("audit-readback", "prep"),
    step("plan-brief", "prep"),
    step("sol-draft", "generate"),
    step("gates", "qa"),
    step("kritika-prepis", "critique"),
    step("rubric-score", "qa"),
    step("delivery", "delivery"),
    step("summary", "delivery")
  ]
};

const SALESPAGE: DfyStepCatalog = {
  gateKey: "salespage",
  version: "salespage@1",
  steps: [
    step("strategy-contract", "prep"),
    step("icp", "prep"),
    step("design-direction", "prep"),
    step("photos", "prep", false),
    step("brief", "prep"),
    step("sol-draft", "generate"),
    step("kritika-prepis", "critique"),
    step("native-round", "critique"),
    step("gates", "qa"),
    step("assembly", "assembly"),
    step("screenshots", "assembly"),
    step("podpis-designera", "qa"),
    step("soulad-se-strategii", "qa"),
    step("delivery", "delivery"),
    step("summary", "delivery")
  ]
};

/** The campaign phases the e-mail builder emits; which of them run depends on
 * the campaign shape, so every `faze-*` is optional. */
export const DFY_CAMPAIGN_PHASES = [
  "faze-warmup",
  "faze-reveal",
  "faze-showup",
  "faze-replay",
  "faze-deadline",
  "faze-downsell",
  "faze-trial",
  "faze-onboarding"
] as const;

const EMAILS: DfyStepCatalog = {
  gateKey: "emails",
  version: "emails@1",
  steps: [
    step("strategy-contract", "prep"),
    step("icp", "prep"),
    step("instance-card", "prep"),
    step("campaign-shape", "prep"),
    ...DFY_CAMPAIGN_PHASES.map((phase) => step(phase, "generate", false)),
    step("kritika-prepis", "critique"),
    step("self-improvement-3-kola", "critique", false),
    step("sending-strategy", "assembly"),
    step("delivery-doc", "assembly", false),
    step("gates", "qa"),
    step("soulad-se-strategii", "qa"),
    step("delivery", "delivery"),
    step("summary", "delivery")
  ]
};

const WEBINAR: DfyStepCatalog = {
  gateKey: "webinar",
  version: "webinar@1",
  steps: [
    step("strategy-contract", "prep"),
    step("icp", "prep"),
    step("instance-card", "prep"),
    step("struktura-timing", "prep"),
    step("sol-brief", "prep"),
    step("kritika-prepis", "critique"),
    step("native-round", "critique"),
    step("outline", "generate"),
    step("gates", "qa"),
    step("soulad-se-strategii", "qa"),
    step("delivery", "delivery"),
    step("summary", "delivery")
  ]
};

export const DFY_STEP_CATALOGS: Record<DfyGateKey, DfyStepCatalog> = {
  strategy: STRATEGY,
  salespage: SALESPAGE,
  emails: EMAILS,
  webinar: WEBINAR
};

export const DFY_GATE_KEYS = Object.keys(DFY_STEP_CATALOGS) as DfyGateKey[];

export function isDfyGateKey(value: string): value is DfyGateKey {
  return Object.prototype.hasOwnProperty.call(DFY_STEP_CATALOGS, value);
}

export function dfyCatalog(gateKey: string): DfyStepCatalog | null {
  return isDfyGateKey(gateKey) ? DFY_STEP_CATALOGS[gateKey] : null;
}

/** Ordered step keys of a gate (catalog order). */
export function dfyCatalogSteps(gateKey: string): string[] {
  return dfyCatalog(gateKey)?.steps.map((entry) => entry.key) ?? [];
}

const STEP_CLASS_BY_KEY = new Map<string, DfyStepClass>();
for (const catalog of Object.values(DFY_STEP_CATALOGS)) {
  for (const entry of catalog.steps) STEP_CLASS_BY_KEY.set(entry.key, entry.stepClass);
}

export function dfyStepClass(step: string): DfyStepClass | null {
  return STEP_CLASS_BY_KEY.get(step) ?? null;
}

/** Every step key any gate knows. Used for label lookup, never for ordering. */
export const DFY_KNOWN_STEPS: ReadonlySet<string> = new Set(STEP_CLASS_BY_KEY.keys());

export function dfyStepLabelKey(step: string): string | null {
  return DFY_KNOWN_STEPS.has(step) ? `staffConsole.dfyBuild.steps.${step}` : null;
}

/**
 * Human label for one reported step: catalog label, campaign-phase template
 * for a phase the catalog does not know yet ("faze-xy" -> "Fáze kampaně: xy"),
 * else the raw runner name (safe: rendered as text, never as markup).
 */
export function dfyStepLabel(step: string, t: (key: string) => string): string {
  const key = dfyStepLabelKey(step);
  if (key) return t(key);
  if (step.startsWith("faze-") && step.length > 5) {
    return t("staffConsole.dfyBuild.steps.faze").replace("{name}", step.slice(5).replace(/-/g, " "));
  }
  return step;
}

/** Catalog order, with runner-reported steps the catalog does not know
 * (new campaign phases) kept where they actually happened. */
export function mergeDfySteps(catalog: readonly string[], reported: readonly string[]): string[] {
  const out: string[] = [];
  let cursor = 0;
  for (const step of reported) {
    const index = catalog.indexOf(step);
    if (index === -1) {
      if (!out.includes(step)) out.push(step);
      continue;
    }
    while (cursor <= index) {
      const next = catalog[cursor++];
      if (!out.includes(next)) out.push(next);
    }
  }
  while (cursor < catalog.length) {
    const next = catalog[cursor++];
    if (!out.includes(next)) out.push(next);
  }
  return out;
}

/**
 * The ordered step list a specific run is measured against: the manifest the
 * runner sent with its first progress post, else the current catalog. A run
 * must never be scored against a catalog it did not run under.
 */
export function dfyRunManifest(gateKey: string, storedManifest: readonly string[] | null): string[] {
  if (storedManifest && storedManifest.length > 0) return [...storedManifest];
  return dfyCatalogSteps(gateKey);
}

/** No heartbeat for this long and the run is not "working", it is silent. */
export const DFY_RUN_STALE_MS = 5 * 60 * 1000;

export function isDfyRunStale(
  run: { status: string; updatedAt: string },
  now = Date.now()
): boolean {
  const updatedAt = Date.parse(run.updatedAt);
  return run.status === "running" && (!Number.isFinite(updatedAt) || now - updatedAt > DFY_RUN_STALE_MS);
}

export interface DfyRunStep {
  step: string;
  ok: boolean;
}

export interface DfyRunProgress {
  /** Manifest order with unknown reported steps kept where they happened. */
  stepKeys: string[];
  totalSteps: number;
  completedSteps: number;
  currentStepKey: string | null;
  /** 1-based position of the current step; equals totalSteps once all ran. */
  currentStepNumber: number;
  /** A reported step outside the manifest: the estimate cannot use the step
   * model for this run, but the partner still sees the honest position. */
  hasUnknownStep: boolean;
}

/**
 * "Krok 11 z 15" derived ONCE, for the partner and the client alike. Both
 * surfaces used to count their own way, so the same run could read as a
 * different step depending on where you looked.
 */
export function dfyRunProgress(
  gateKey: string,
  reported: readonly DfyRunStep[],
  storedManifest: readonly string[] | null = null
): DfyRunProgress {
  const manifest = dfyRunManifest(gateKey, storedManifest);
  const stepKeys = mergeDfySteps(manifest, reported.map((entry) => entry.step));
  const done = new Set(reported.filter((entry) => entry.ok).map((entry) => entry.step));
  const completedSteps = stepKeys.filter((step) => done.has(step)).length;
  // The runner names the step it is ON by reporting it not-ok; without that
  // the current step is simply the first one still missing.
  const explicitCurrent = reported.find((entry) => !entry.ok && !done.has(entry.step))?.step;
  const currentStepKey = explicitCurrent ?? stepKeys.find((step) => !done.has(step)) ?? null;
  const index = currentStepKey ? stepKeys.indexOf(currentStepKey) : -1;
  return {
    stepKeys,
    totalSteps: stepKeys.length,
    completedSteps,
    currentStepKey,
    currentStepNumber: index >= 0 ? index + 1 : stepKeys.length,
    hasUnknownStep: reported.some((entry) => !manifest.includes(entry.step))
  };
}
